import apiClient from './apiClient';

export interface BudgetSummary {
  totalAllocated: number;
  totalSpent: number;
  totalCommitted: number;
  remaining: number;
  utilizationPercentage: number;
  overBudgetCount: number;
  projectedOverrunCount: number;
  costCodes: CostCodeForecastRow[];
}

export interface CommittedBreakdown {
  projectId: string;
  totalCommitted: number;
  byStatus: Record<string, number>;
}

export interface CashFlowPoint {
  month: string;
  actual: number;
  projected: number;
}

export interface VarianceRow {
  id: string;
  projectId: string;
  phaseId?: string | null;
  budgetId?: string | null;
  varianceType: string;
  category?: string | null;
  periodStart: string;
  periodEnd: string;
  projectedValue: number;
  actualValue: number;
  varianceAmount: number;
  variancePercentage: number;
  threshold: number;
  status: string;
  isSignificant: boolean;
  analysisNotes?: string | null;
  actionTaken?: string | null;
  createdAt?: string;
}

export interface PaymentRow {
  id: string;
  taskId?: string | null;
  teamId?: string | null;
  amount: number;
  status: string;
  paidAt?: string | null;
  createdAt?: string;
}

export interface PaymentSummary {
  pending: number;
  approved: number;
  paid: number;
  totalPending: number;
  totalApproved: number;
  totalPaid: number;
  rows: PaymentRow[];
}

export interface CostCodeForecastRow {
  costCodeId: string;
  code: string;
  name: string;
  category?: string | null;
  budget: number;
  actual: number;
  committed: number;
  available: number;
  defaultCtc: number;
  ctc: number;
  ctcOverride?: number | null;
  eac: number;
  projectedOverrun: boolean;
  overrunAmount: number;
}

type Envelope<T> = { success?: boolean; data?: T; error?: string };

const unwrap = <T>(response: unknown): T => {
  const r = response as { data?: unknown; status?: number };
  const body = typeof r?.status === 'number' ? r.data : response;
  const env = body as Envelope<T>;
  return env && Object.prototype.hasOwnProperty.call(env, 'data')
    ? (env.data as T)
    : (body as T);
};

const buildQuery = (filters: Record<string, unknown>): string => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    params.append(key, String(value));
  });
  const qs = params.toString();
  return qs ? `?${qs}` : '';
};

class FinancialsService {
  async getBudgetSummary(projectId: string): Promise<BudgetSummary> {
    const response = await apiClient.get<Envelope<BudgetSummary>>(`/budgets/summary${buildQuery({ projectId })}`);
    return unwrap<BudgetSummary>(response);
  }

  async getCommittedBreakdown(projectId: string): Promise<CommittedBreakdown> {
    const response = await apiClient.get<Envelope<CommittedBreakdown>>(`/budgets/committed${buildQuery({ projectId })}`);
    return unwrap<CommittedBreakdown>(response);
  }

  async getCostCodeForecast(projectId: string): Promise<CostCodeForecastRow[]> {
    const response = await apiClient.get<Envelope<CostCodeForecastRow[]>>(`/budgets/cost-codes/forecast${buildQuery({ projectId })}`);
    return unwrap<CostCodeForecastRow[]>(response) ?? [];
  }

  async setCostToCompleteOverride(
    projectId: string,
    costCodeId: string,
    ctcAmount: number,
    reason?: string
  ): Promise<CostCodeForecastRow> {
    const response = await apiClient.post<Envelope<CostCodeForecastRow>>(
      `/budgets/cost-codes/${costCodeId}/ctc-override${buildQuery({ projectId })}`,
      { ctcAmount, reason }
    );
    return unwrap<CostCodeForecastRow>(response);
  }

  async getCashFlowComparison(projectId: string, months = 12): Promise<CashFlowPoint[]> {
    const response = await apiClient.get<Envelope<CashFlowPoint[]>>(`/cash-flow/comparison${buildQuery({ projectId, months })}`);
    return unwrap<CashFlowPoint[]>(response) ?? [];
  }

  async listVariances(projectId: string, filters: { significantOnly?: boolean; from?: string; to?: string } = {}): Promise<VarianceRow[]> {
    const response = await apiClient.get<Envelope<VarianceRow[]>>(`/variances${buildQuery({ projectId, ...filters })}`);
    return unwrap<VarianceRow[]>(response) ?? [];
  }

  async getPaymentSummary(projectId: string): Promise<PaymentSummary> {
    const response = await apiClient.get<Envelope<PaymentSummary>>(`/payments/summary${buildQuery({ projectId })}`);
    return unwrap<PaymentSummary>(response);
  }
}

export default new FinancialsService();
